import React, { useState, useEffect } from 'react'
import {
    Button,
    Card,
    Form,
    Input,
    Switch,
    message,
    Divider,
    Typography,
    Space,
} from 'antd'
import { CopyOutlined, ReloadOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons'
import deviceApi from '../services/api'
import './manage.css'

const { TextArea } = Input
const { Text } = Typography


function Manage() {
    // 用户信息
    const [profileForm] = Form.useForm()
    const [profileLoading, setProfileLoading] = useState(false)
    const [profileSaving, setProfileSaving] = useState(false)
    const [usernameStatus, setUsernameStatus] = useState('')
    const [usernameHelp, setUsernameHelp] = useState('')
    const [originalUsername, setOriginalUsername] = useState('')

    // 配置管理
    const [configForm] = Form.useForm()
    const [configLoading, setConfigLoading] = useState(false)
    const [configSaving, setConfigSaving] = useState(false)
    const [tokenKey, setTokenKey] = useState('')
    const [tokenVisible, setTokenVisible] = useState(false)
    const [tokenRegenerating, setTokenRegenerating] = useState(false)

    useEffect(() => {
        loadProfile()
        loadConfig()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // 加载用户信息
    const loadProfile = async () => {
        setProfileLoading(true)
        try {
            const data = await deviceApi.getMyProfile()
            setOriginalUsername(data.username || '')
            profileForm.setFieldsValue({
                username: data.username || '',
                name: data.name || '',
                bio: data.bio || '',
                is_public: data.is_public !== false,
            })
        } catch (error) {
            message.error(`加载用户信息失败: ${error.message}`)
        } finally {
            setProfileLoading(false)
        }
    }

    // 加载配置
    const loadConfig = async () => {
        setConfigLoading(true)
        try {
            const data = await deviceApi.getConfig()
            configForm.setFieldsValue({
                ai_context: data.ai_context || '',
                daily_tips: data.daily_tips || '',
                quick_links: data.quick_links || '',
            })
            setTokenKey(data.token_key || '')
        } catch (error) {
            message.error(`加载配置失败: ${error.message}`)
        } finally {
            setConfigLoading(false)
        }
    }

    // 检查用户名唯一性
    const handleUsernameCheck = async (username) => {
        if (!username || username === originalUsername) {
            setUsernameStatus('')
            setUsernameHelp('')
            return
        }
        try {
            const res = await deviceApi.checkUsername(username)
            if (res.available) {
                setUsernameStatus('success')
                setUsernameHelp('用户名可用')
            } else {
                setUsernameStatus('error')
                setUsernameHelp('用户名已被占用')
            }
        } catch {
            setUsernameStatus('')
            setUsernameHelp('')
        }
    }

    // 保存用户信息
    const handleProfileSave = async () => {
        try {
            const values = await profileForm.validateFields()
            if (usernameStatus === 'error') {
                message.error('用户名已被占用，请更换')
                return
            }
            setProfileSaving(true)
            await deviceApi.updateProfile({
                username: values.username,
                name: values.name || '',
                bio: values.bio || '',
                is_public: values.is_public !== false,
            })
            setOriginalUsername(values.username)
            message.success('用户信息保存成功')
        } catch (error) {
            if (error.errorFields) return
            message.error(`保存失败: ${error.message}`)
        } finally {
            setProfileSaving(false)
        }
    }

    // 重新生成 token_key
    const handleRegenerateToken = async () => {
        setTokenRegenerating(true)
        try {
            const res = await deviceApi.regenerateToken()
            setTokenKey(res.token_key || '')
            setTokenVisible(true)
            message.success('Token 已重新生成')
        } catch (error) {
            message.error(`生成失败: ${error.message}`)
        } finally {
            setTokenRegenerating(false)
        }
    }

    // 复制 token_key
    const handleCopyToken = () => {
        if (!tokenKey) return
        navigator.clipboard.writeText(tokenKey).then(() => {
            message.success('Token 已复制')
        }).catch(() => {
            message.error('复制失败，请手动复制')
        })
    }

    // 保存配置
    const handleConfigSave = async () => {
        try {
            const values = await configForm.validateFields()
            setConfigSaving(true)
            await deviceApi.saveConfig({
                ai_context: values.ai_context || '',
                daily_tips: values.daily_tips || '',
                quick_links: values.quick_links || '',
            })
            message.success('配置保存成功')
        } catch (error) {
            if (error.errorFields) return
            message.error(`保存失败: ${error.message}`)
        } finally {
            setConfigSaving(false)
        }
    }

    return (
        <div className='manage-container'>
            <h1>个人设置</h1>

            {/* 用户信息区块 */}
            <Card title="用户信息" className="manage-section" loading={profileLoading}>
                <Form
                    form={profileForm}
                    layout="vertical"
                    initialValues={{ is_public: true }}
                >
                    <Form.Item
                        label="用户名"
                        name="username"
                        validateStatus={usernameStatus}
                        help={usernameHelp}
                        rules={[{ required: true, message: '请输入用户名' }]}
                    >
                        <Input
                            placeholder="输入用户名"
                            onChange={e => {
                                const val = e.target.value.trim()
                                handleUsernameCheck(val)
                            }}
                        />
                    </Form.Item>
                    <Form.Item label="姓名" name="name">
                        <Input placeholder="输入你的姓名或昵称" />
                    </Form.Item>
                    <Form.Item label="个人简介" name="bio">
                        <TextArea
                            autoSize={{ minRows: 2, maxRows: 4 }}
                            placeholder="简单介绍一下自己..."
                        />
                    </Form.Item>
                    <Form.Item label="公开记录" name="is_public" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" onClick={handleProfileSave} loading={profileSaving}>
                            保存用户信息
                        </Button>
                    </Form.Item>
                </Form>
            </Card>

            {/* 配置管理区块 */}
            <Card title="配置管理" className="manage-section" loading={configLoading}>
                <p style={{ color: '#666', marginBottom: 16 }}>
                    个人上下文供外部AI读取时参考，常驻文案和链接显示在编辑页面
                </p>

                {/* API Token Key */}
                <div style={{ marginBottom: 24 }}>
                    <div style={{ fontWeight: 500, marginBottom: 8 }}>API Token Key</div>
                    <p style={{ color: '#999', fontSize: 13, marginBottom: 8 }}>
                        用于外部AI通过开放API读取/写入你的数据。
                        文档地址：<a href="/api/open/docs" target="_blank" rel="noopener noreferrer">/api/open/docs</a>
                    </p>
                    <Space>
                        <Input
                            value={tokenKey
                                ? (tokenVisible ? tokenKey : tokenKey.slice(0, 8) + '••••••••••••••••••••••••')
                                : '（尚未生成）'}
                            readOnly
                            style={{ width: 360, fontFamily: 'monospace' }}
                        />
                        <Button
                            icon={tokenVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                            onClick={() => setTokenVisible(v => !v)}
                            disabled={!tokenKey}
                        />
                        <Button
                            icon={<CopyOutlined />}
                            onClick={handleCopyToken}
                            disabled={!tokenKey}
                        >
                            复制
                        </Button>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={handleRegenerateToken}
                            loading={tokenRegenerating}
                            danger
                        >
                            {tokenKey ? '重新生成' : '生成 Token'}
                        </Button>
                    </Space>
                </div>

                <Divider />

                <Form form={configForm} layout="vertical" onFinish={handleConfigSave}>
                    <Form.Item label="个人上下文" name="ai_context">
                        <TextArea
                            autoSize={{ minRows: 6, maxRows: 15 }}
                            placeholder="输入个人背景、习惯、偏好等，供外部AI读取时作为参考..."
                        />
                    </Form.Item>
                    <Form.Item label="常驻文案" name="daily_tips">
                        <TextArea
                            autoSize={{ minRows: 6, maxRows: 15 }}
                            placeholder="输入要在编辑页面顶部显示的常驻文案内容..."
                        />
                    </Form.Item>
                    <Form.Item label="常驻链接" name="quick_links">
                        <TextArea
                            autoSize={{ minRows: 3, maxRows: 8 }}
                            placeholder="每行一个链接，格式：链接文本|链接地址"
                        />
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" htmlType="submit" loading={configSaving}>
                            保存配置
                        </Button>
                    </Form.Item>
                </Form>
            </Card>
        </div>
    )
}

export default Manage